'use client';

import { PublicKey } from '@velocity-exchange/sdk';
import { BigNum, LAMPORTS_EXP } from '@velocity-exchange/sdk';
import { Connection } from '@solana/web3.js';
import { useEffect, useRef } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useCurrentWalletAdapter from './useCurrentWalletAdapter';
import useCurrentWalletConnectionState from './useCurrentWalletConnectionState';
import useInterval from './useInterval';

/**
 * !! This hook will keep the wallet balance in the store up to date. use drift account store to GET the current balance in a UI component
 * @returns
 */
const useSolBalance = () => {
	const listenerId = useRef<number | null>(null);
	const connection = useDriftStore((s) => s.connection?.current) as Connection;
	const wallet = useCurrentWalletAdapter();
	const connected = wallet?.connected;
	const set = useDriftStore((s) => s.set);
	const currentSolBalance = useDriftStore((s) => s.wallet.currentSolBalance);

	const currentWalletConnectionState = useCurrentWalletConnectionState();

	const handleNewBalance = (newBalance: number) => {
		if (newBalance === currentSolBalance.toNum() && newBalance !== 0) return;

		set((s) => {
			s.wallet.currentSolBalance = BigNum.from(newBalance, LAMPORTS_EXP);
		});

		currentWalletConnectionState.instance.progress('BalanceLoaded');
	};

	const getBalance = async (authority: PublicKey) => {
		const accountInfo = await connection.getAccountInfo(authority);

		const newBalance = accountInfo ? accountInfo.lamports : 0;

		handleNewBalance(newBalance);
	};

	const pubKeyForPreviousListener = useRef<PublicKey | null>(null);

	const updateBalance = () => {
		if (connected && connection && wallet?.publicKey) {
			getBalance(wallet.publicKey);

			if (
				pubKeyForPreviousListener.current &&
				pubKeyForPreviousListener.current.equals(wallet.publicKey)
			) {
				// Don't create a new listener if the public key is the same as the previous one
				return;
			}

			// Clean up old listener before creating a new one (e.g. authority changed)
			if (listenerId.current !== null) {
				connection.removeAccountChangeListener(listenerId.current);
			}

			const newListenerId = connection.onAccountChange(
				wallet.publicKey,
				(accountInfo) => {
					handleNewBalance(accountInfo.lamports);
				}
			);

			listenerId.current = newListenerId;
			pubKeyForPreviousListener.current = wallet.publicKey;
		} else if (connection) {
			// Clear the balance and listener if we're not "connected" or there is no wallet public key to be subscribed to anymore

			set((s) => {
				s.wallet.currentSolBalance = BigNum.zero(LAMPORTS_EXP);
			});

			if (listenerId.current === null) return;

			connection.removeAccountChangeListener(listenerId.current);
			listenerId.current = null;
		}
	};

	useEffect(() => {
		updateBalance();
	}, [
		connected,
		connection,
		currentWalletConnectionState,
		wallet?.publicKey?.toBase58(),
	]);

	useInterval(() => {
		if (
			currentWalletConnectionState.instance.AdapterConnected &&
			!currentWalletConnectionState.instance.BalanceLoaded
		) {
			updateBalance();
		}
	}, 1000);

	return;
};

export default useSolBalance;
